import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, MessageSquarePlus, UserRound } from 'lucide-react';
import type { ActivityKind, DomainEntityType } from '@clawhire/shared';
import { todayIso } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import { SkeletonLines } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { toast } from '@/components/ui/toaster';

export interface ActivityEntry {
  id: string;
  /** YYYY-MM-DD */
  date: string;
  /** Markdown */
  text: string;
  kind: ActivityKind | string;
  actor?: string | { name?: string | null; email?: string | null } | null;
  createdAt?: string;
}

export interface ActivityTimelineProps {
  entityType: DomainEntityType;
  entityId: string;
  /** Pass the entries when the detail endpoint already includes `activity[]`; otherwise they are fetched. */
  entries?: ActivityEntry[];
  /** Hide the "Add note" form (viewer role). */
  canAdd?: boolean;
  /** Called after a note is added (e.g. to refetch the detail). The activity query is invalidated anyway. */
  onChanged?: () => void;
  className?: string;
  maxItems?: number;
}

function actorName(actor: ActivityEntry['actor']): string | null {
  if (!actor) return null;
  if (typeof actor === 'string') return actor;
  return actor.name ?? actor.email ?? null;
}

function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

/**
 * Activity log as "**DD.MM.YYYY** – text" lines (markdown), newest first, with an inline
 * "Add note" form that posts to `POST /activity`.
 */
export function ActivityTimeline({
  entityType,
  entityId,
  entries: entriesProp,
  canAdd = true,
  onChanged,
  className,
  maxItems,
}: ActivityTimelineProps) {
  const queryClient = useQueryClient();
  const key = queryKeys.activity(entityType, entityId);
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrapList<ActivityEntry>(await api.get<unknown>('/activity', { query: { entityType, entityId } })),
    enabled: entriesProp === undefined,
  });
  const entries = React.useMemo(() => {
    const list = [...(entriesProp ?? query.data ?? [])];
    list.sort((a, b) =>
      a.date < b.date ? 1 : a.date > b.date ? -1 : (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
    );
    return maxItems ? list.slice(0, maxItems) : list;
  }, [entriesProp, query.data, maxItems]);

  const [text, setText] = React.useState('');
  const [date, setDate] = React.useState<string | null>(todayIso());
  const [open, setOpen] = React.useState(false);

  const add = useMutation({
    mutationFn: () =>
      api.post('/activity', { entityType, entityId, text: text.trim(), date: date ?? undefined }),
    onSuccess: async () => {
      setText('');
      setDate(todayIso());
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: key });
      onChanged?.();
      toast.success('Note added');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const isLoading = entriesProp === undefined && query.isLoading;

  return (
    <section className={cn('space-y-4', className)} aria-label="Activity">
      {canAdd ? (
        <div>
          {open ? (
            <form
              className="space-y-3 rounded-lg border p-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (text.trim()) add.mutate();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor={`activity-note-${entityId}`}>Note (markdown)</Label>
                <Textarea
                  id={`activity-note-${entityId}`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="What happened? e.g. Client feedback received: positive, interview on Monday."
                  rows={3}
                  autoFocus
                  maxLength={4000}
                />
              </div>
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`activity-date-${entityId}`}>Date</Label>
                  <DateInput id={`activity-date-${entityId}`} value={date} onChange={setDate} />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={add.isPending} disabled={!text.trim()}>
                    Add note
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
              <MessageSquarePlus aria-hidden="true" /> Add note
            </Button>
          )}
        </div>
      ) : null}

      {isLoading ? (
        <SkeletonLines lines={4} />
      ) : entries.length === 0 ? (
        <EmptyState
          compact
          title="No activity yet"
          description="Notes and automatic entries will show up here."
        />
      ) : (
        <ol className="relative space-y-4 border-l pl-5">
          {entries.map((entry) => {
            const who = actorName(entry.actor);
            const auto = entry.kind === 'AUTO';
            return (
              <li key={entry.id} className="relative">
                <span
                  className={cn(
                    'absolute -left-[1.45rem] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border bg-background',
                    auto ? 'text-muted-foreground' : 'text-primary',
                  )}
                  aria-hidden="true"
                >
                  {auto ? <Bot className="h-2.5 w-2.5" /> : <UserRound className="h-2.5 w-2.5" />}
                </span>
                <div className="prose prose-sm max-w-none text-foreground dark:prose-invert prose-p:my-0 prose-ul:my-1 prose-li:my-0">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                  >{`**${formatDate(entry.date)}** – ${entry.text}`}</ReactMarkdown>
                </div>
                {who || auto ? (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {auto ? 'Automatic' : who}
                    {auto && who ? ` · ${who}` : ''}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
